<!-- active pages component for dashboard landing -->
<template>
  <div class="card dashboard-card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div class="header-text">
        <div class="title-row">
          <h4 class="mb-0">Active Pages</h4>

          <info-box class="info" :leftOffset="-10">
            <div class="info-content">
              <div class="info-title">What this card shows</div>
              <ul class="info-list">
                <li>
                  Pages that are
                  <strong>linked in Navigation</strong> (therefore live on the
                  Touchscreen).
                </li>
                <li>
                  Click a <strong>Page</strong> row to open its edit screen.
                </li>
                <li>
                  Use the <strong>dropdown</strong> to see the linked child
                  pages (pages that appear as the subnavigation within a page).
                </li>
                <li>
                  <strong>Live Route</strong> items are shown but disabled (no
                  dashboard page to edit).
                </li>
              </ul>
            </div>
          </info-box>
        </div>
        <small class="text-muted">{{ activeCount }} Live Sensoreal Pages</small>
      </div>

      <!-- <button
        class="btn btn-sm btn-outline-primary"
        @click="openPages"
        title="Open Pages Overview"
      >
        View All Pages
      </button> -->
    </div>

    <div class="card-body">
      <div v-if="!categoryTabs.length" class="text-muted">
        No navigation categories found.
      </div>

      <div v-else>
        <!-- Category tabs -->
        <!-- most projects only have the one category, so unsure how useful this is -->
        <div class="tabs">
          <button
            v-for="t in categoryTabs"
            :key="t.id"
            class="tab"
            :class="{ 'tab--active': t.id === activeCategoryId }"
            @click="activeCategoryId = t.id"
            type="button"
          >
            {{ t.label }}
            <span class="tab__count">{{ t.count }}</span>
          </button>
        </div>

        <div
          v-if="
            !groupsByCategory[activeCategoryId] ||
              !groupsByCategory[activeCategoryId].length
          "
          class="text-muted"
        >
          No linked pages in this category.
        </div>

        <div v-else class="list">
          <!-- Base group -->
          <div
            v-for="group in groupsByCategory[activeCategoryId]"
            :key="group.key"
            class="group"
          >
            <!-- Base row -->
            <div class="base-row">
              <button
                class="list-item list-item--base"
                :class="{
                  'list-item--disabled':
                    group.baseTarget && group.baseTarget.type === 'route'
                }"
                type="button"
                @click="onBaseNavigate(group)"
              >
                <div class="list-item__title">
                  {{ group.baseTitle }}
                </div>

                <div class="list-item__meta">
                  <span v-html="group.baseNavLabel"></span>

                  <span v-if="group.basePill" class="list-item__pill">
                    {{ group.basePill }}
                  </span>
                </div>
              </button>

              <!-- Chevron toggle (only if children exist) -->
              <button
                v-if="group.children && group.children.length"
                class="chev"
                type="button"
                @click.stop="toggleExpanded(group.key)"
                :aria-label="isExpanded(group.key) ? 'Collapse' : 'Expand'"
              >
                <fa
                  :icon="[
                    'fas',
                    isExpanded(group.key) ? 'chevron-up' : 'chevron-down'
                  ]"
                />
              </button>

              <!-- Spacer to keep alignment when there are NO children -->
              <div v-else class="chev-spacer"></div>
            </div>

            <!-- Children dropdown -->
            <div
              v-if="
                group.children && group.children.length && isExpanded(group.key)
              "
              class="children"
            >
              <button
                v-for="child in group.children"
                :key="child.key"
                class="list-item list-item--child"
                :class="{ 'list-item--disabled': child.type === 'route' }"
                type="button"
                @click="child.type === 'page' && goToTarget(child)"
              >
                <div class="list-item__title">{{ child.title }}</div>
                <div class="list-item__meta">
                  <span v-html="child.navLabel"></span>
                  <span v-if="child.pill" class="list-item__pill">{{
                    child.pill
                  }}</span>
                </div>
              </button>
            </div>
          </div>
          <!-- /group -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { FETCH_NAVIGATION, FETCH_PAGES } from "@/store/actions.type";
import InfoBox from "@/components/InfoBox";

export default {
  name: "DashboardActivePages",
  components: { InfoBox },

  data() {
    return {
      activeCategoryId: null,
      expandedKeys: {} // { [groupKey]: true/false }
    };
  },

  computed: {
    ...mapGetters(["navigation", "pages", "activeLanguage"]),

    langCode() {
      return this.activeLanguage && this.activeLanguage.code
        ? this.activeLanguage.code
        : "en";
    },

    categoriesRaw() {
      return this.navigation && this.navigation.categories
        ? this.navigation.categories
        : [];
    },

    categoriesOrdered() {
      // sort by category.order if present, otherwise keep original order
      var cats = (this.categoriesRaw || []).slice();

      cats.sort(function(a, b) {
        var ao =
          a && a.order !== undefined && a.order !== "" ? Number(a.order) : null;
        var bo =
          b && b.order !== undefined && b.order !== "" ? Number(b.order) : null;

        if (ao === null && bo === null) return 0;
        if (ao === null) return 1;
        if (bo === null) return -1;
        return ao - bo;
      });

      return cats;
    },

    pagesById() {
      var m = {};
      (this.pages || []).forEach(function(p) {
        if (p && p._id) m[p._id] = p;
      });
      return m;
    },

    groupsByCategory() {
      // groups: base + children nested
      var out = {};
      var self = this;

      this.categoriesOrdered.forEach(function(cat) {
        out[cat.id] = [];

        (cat.children || []).forEach(function(base) {
          var group = self.makeBaseGroup(base);
          // include group if base itself is active OR it has any active children
          if (group.baseIsActive || (group.children && group.children.length)) {
            out[cat.id].push(group);
          }
        });

        // keep nav order top->bottom based on base.order / child.order (matches Navigation)
        out[cat.id].sort(function(a, b) {
          return a.baseOrder - b.baseOrder;
        });
      });

      return out;
    },

    activeCount() {
      var total = 0;
      var groups = this.groupsByCategory || {};
      Object.keys(groups).forEach(function(catId) {
        (groups[catId] || []).forEach(function(g) {
          if (g.baseIsActive) total += 1;
          total += g.children ? g.children.length : 0;
        });
      });
      return total;
    },

    categoryTabs() {
      var self = this;

      return this.categoriesOrdered.map(function(cat) {
        var label = "(Category)";
        if (cat && cat.label) {
          if (cat.label[self.langCode] !== undefined)
            label = cat.label[self.langCode];
          else if (cat.label.en !== undefined) label = cat.label.en;
          else if (typeof cat.label === "string") label = cat.label;
        }

        // count = base active + active children
        var groups = self.groupsByCategory[cat.id] || [];
        var count = 0;
        groups.forEach(function(g) {
          if (g.baseIsActive) count += 1;
          count += g.children ? g.children.length : 0;
        });

        return { id: cat.id, label: label, count: count };
      });
    }
  },

  created() {
    this.$store.dispatch(FETCH_NAVIGATION);
    this.$store.dispatch(FETCH_PAGES);
  },

  watch: {
    categoryTabs: {
      immediate: true,
      handler: function(tabs) {
        if (!this.activeCategoryId && tabs && tabs.length) {
          this.activeCategoryId = tabs[0].id; // already in left->right order
        }
      }
    }
  },

  methods: {
    openPages() {
      this.$router.push(`/pages`);
    },

    // ---------- label helpers ----------
    getNavLabel(navItem) {
      if (!navItem || !navItem.label) return "(Nav item)";
      if (navItem.label[this.langCode] !== undefined)
        return navItem.label[this.langCode];
      if (navItem.label.en !== undefined) return navItem.label.en;
      if (typeof navItem.label === "string") return navItem.label;
      return "(Nav item)";
    },

    getPageName(page) {
      if (page && page.name) return page.name;
      if (page && page.heading) {
        if (page.heading[this.langCode]) return page.heading[this.langCode];
        if (page.heading.en) return page.heading.en;
      }
      return "(Page)";
    },

    // ---------- routing helpers ----------
    goToTarget(row) {
      // only pages are clickable in dashboard
      if (row.type === "page") {
        this.$router.push("/pages/" + row.pageId);
      }
    },

    // ---------- build group ----------
    makeBaseGroup(base) {
      var baseNavLabel = this.getNavLabel(base);

      // base target
      var baseTarget = this.getTargetFromNavItem(base);
      var baseIsActive = baseTarget !== null;

      var baseTitle = baseIsActive ? baseTarget.title : baseNavLabel;

      // children targets (only include active children)
      var childrenOut = [];
      var self = this;

      (base.children || []).forEach(function(child) {
        var target = self.getTargetFromNavItem(child);
        if (!target) return;

        childrenOut.push({
          key:
            (child.id || child.pageId || "route") +
            "-" +
            (child.pageId || target.title),
          title: target.title,
          navLabel: self.getNavLabel(child),
          type: target.type,
          pageId: target.pageId,
          pill: target.pill,
          order:
            child.order !== undefined && child.order !== ""
              ? Number(child.order)
              : 999
        });
      });

      // keep child order top->bottom
      childrenOut.sort(function(a, b) {
        return a.order - b.order;
      });

      var baseOrder =
        base.order !== undefined && base.order !== ""
          ? Number(base.order)
          : 999;

      return {
        key: "base-" + (base.id || base.pageId || baseNavLabel),
        baseOrder: baseOrder,
        baseNavLabel: baseNavLabel,
        baseIsActive: baseIsActive,
        baseTitle: baseTitle,
        baseTarget: baseTarget, // {type, pageId}
        basePill: baseTarget ? baseTarget.pill : null,
        children: childrenOut
      };
    },

    getTargetFromNavItem(item) {
      // priority:
      // 1) pageId (edit page in dashboard)
      // 2) customRoute (live route — display only)
      // 3) externalUrl (display only)
      if (!item) return null;

      // pageId target
      if (item.pageId && this.pagesById[item.pageId]) {
        var page = this.pagesById[item.pageId];
        return {
          type: "page",
          pageId: item.pageId,
          title: this.getPageName(page),
          pill: "Page"
        };
      }

      // customRoute target (count + show, not clickable)
      if (item.customRoute && item.customRoute !== "") {
        return {
          type: "route",
          title: this.getNavLabel(item),
          pill: "Live Route"
        };
      }

      // externalUrl target (count + show, not clickable)
      if (item.externalUrl && item.externalUrl !== "") {
        return {
          type: "route",
          title: this.getNavLabel(item),
          pill: "External"
        };
      }

      return null;
    },

    // ---------- expand / collapse ----------
    isExpanded(groupKey) {
      return !!this.expandedKeys[groupKey];
    },

    toggleExpanded(groupKey) {
      this.$set(this.expandedKeys, groupKey, !this.expandedKeys[groupKey]);
    },

    onBaseNavigate(group) {
      if (!group.baseTarget) return;

      // only navigate for pages
      if (group.baseTarget.type === "page") {
        this.goToTarget(group.baseTarget);
      }
      // routes are intentionally non-clickable
    }
  }
};
</script>

<style lang="scss" scoped>
.dashboard-card {
  height: 100%;
}

.tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
  margin-bottom: 10px;
}

.tab {
  border: 1px solid #26c281;
  background: #fff;
  color: #26c281;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
}

.tab--active {
  background: #26c281;
  border-color: #26c281;
  color: #fff;
}

.tab__count {
  margin-left: 6px;
  opacity: 0.8;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 360px;
  overflow-y: auto;
}

.group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.list-item {
  text-align: left;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
}

.list-item:hover {
  background: rgba(226, 232, 240, 0.5);
}

.list-item__title {
  font-weight: 600;
  font-size: 13px;
}

.list-item__meta {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
  display: flex;
  gap: 8px;
  align-items: center;
}

.list-item__pill {
  font-size: 11px;
  border: 1px solid #e5e7eb;
  padding: 2px 6px;
  border-radius: 999px;
  color: #374151;
  background: #f9fafb;
}

.children {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 14px;
}

.base-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.list-item--base {
  flex: 1;
}

.chev {
  width: 42px;
  color: #26c281;
  border: 1px solid #26c281;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.chev-spacer {
  width: 42px; /* same as .chev */
}

.chev:hover {
  background: rgba(148, 255, 178, 0.5);
}

/* non-clickable (live/external) rows */
.list-item--disabled {
  cursor: default;
  pointer-events: none;
  opacity: 0.75;
}

.list-item--disabled:hover {
  background: #fff;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

// .info {
//   display: inline-flex;
//   align-items: center;
//   line-height: 1;
// }

// .info :global(.info-box__btn) {
//   position: relative;
//   top: 2px;
// }
</style>

<style lang="scss">
/* Active Pages header InfoBox alignment (global so it reaches InfoBox.vue) */
.title-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

.info {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.info .info-box__btn {
  position: relative;
  top: 1px;
}

/* Make icon match header tone */
.info .info-box {
  color: #6b7280;
}
.info .info-box:hover,
.info .info-box--active {
  color: #0d6efd;
}

/* Make modal feel like a small “help tooltip”, not a big white slab */
.info .info-box__modal {
  width: 320px; /* smaller than default 360 */
  padding: 14px 16px; /* slightly tighter */
  font-size: 13px; /* calmer */
  line-height: 1.45;
}

/* Nice formatting for our structured content */
.info .info-title {
  font-weight: 700;
  margin-bottom: 8px;
}

.info .info-list {
  margin: 0;
  padding-left: 18px;
}

.info .info-list li {
  margin-bottom: 6px;
}

.info .info-list li:last-child {
  margin-bottom: 0;
}
</style>
